import * as React from 'react'
import Card from 'material-ui/Card/Card'
import Divider from 'material-ui/Divider'
import Tab from 'material-ui/Tabs/Tab'
import Tabs from 'material-ui/Tabs/Tabs'
import TextField from 'material-ui/TextField'
import {ContentAdd} from 'material-ui/svg-icons'
import AppBar from 'material-ui/AppBar'
import {url} from '../../App/AuthenticatedRoute'
import {OrderDetailsState} from './OrderDetailsState'
import {OrderDetailsAction} from './OrderDetailsActions'
import {EditableButtons} from '../../View/EditableButtons'
import FlatButton from 'material-ui/FlatButton'
import {DateTimeFormatter} from 'js-joda'
import {OrderProtocolWithDetails} from '../../Entities/OrderProtocolWithDetails'
import {OrderProtocolDetailType} from '../../Entities/OrderProtocolDetail'
import {PositionsTabView} from './Positions/PositionsView'
import {InvoiceTabView} from './Invoice/InvoiceView'
import {DeliveryTabView} from './Delivery/DeliveryView'
import {ParcelsTabView} from './Parcels/ParcelsView'
import {GeneralInformationTabView} from './GeneralInformation/GeneralInformationView'
import {MenuAndUpButton} from '../../View/MenuAndUpButton'
import injectSheet from 'react-jss'

interface Props {
    readonly state: OrderDetailsState
    readonly dispatch: (action: OrderDetailsAction) => void
}

const styles = {
    root: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
    },
    orderWrapper: {
        overflowY: 'scroll',
        flex: '1'
    },
    tabsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1',
        overflow: 'hidden'
    },
    tabsContent: {
        padding: '32px',
        boxSizing: 'border-box'
    }
}

export const OrderDetailsView =
    injectSheet<Props>(styles)(
        ({classes, state, dispatch}) => (
            <div className={`order ${classes.root}`}>
                <div className="navigation">
                    <AppBar
                        style={{boxShadow: 'none'}}
                        title={`Auftrag #${state.orderWithDetails.order.orderNumber}`}
                        className="navigation__bar"
                        iconElementLeft={
                            <MenuAndUpButton
                                onMenuButtonClicked={() => dispatch({
                                    type: 'MenuButtonClicked',
                                })}
                                upUrl={url({type: 'OrderOverview'})}
                            />}
                    />
                </div>
                <Tabs
                    className={`tabs ${classes.tabsWrapper}`}
                    inkBarStyle={{height: '4px', marginTop: '-4px'}}
                    tabItemContainerStyle={{
                        boxShadow: '0px 1px 6px rgba(0, 0, 0, 0.12), 0px 1px 4px rgba(0, 0, 0, 0.12)'
                    }}
                    contentContainerStyle={{
                        overflow: 'scroll',
                        boxSizing: 'border-box'
                    }}
                    value={state.activeTab}
                    onChange={value => dispatch({
                        type: 'ActiveTabChanged',
                        value,
                    })}
                >
                    <Tab
                        label="Übersicht"
                        className="tabs__tab"
                        value="GeneralInformation"
                    >
                        <GeneralInformationTabView
                            orderWithDetails={state.orderWithDetails}
                            state={state.generalInformationTab}
                            dispatch={
                                subAction => dispatch({
                                    type: 'GeneralInformationAction',
                                    subAction,
                                })}
                        />
                    </Tab>
                    <Tab
                        label="Positionen"
                        className="tabs__tab"
                        value="Positions"
                    >
                        <PositionsTabView
                            active={state.activeTab === 'Positions'}
                            state={state.positionsTab}
                            dispatch={
                                subAction => dispatch({
                                    type: 'PositionsAction',
                                    subAction,
                                })}
                        />
                    </Tab>
                    <Tab
                        label="Rechnung"
                        className="tabs__tab"
                        value="Invoice"
                    >
                        <InvoiceTabView
                            active={state.activeTab === 'Invoice'}
                            state={state.invoiceTab}
                            dispatch={
                                subAction => dispatch({
                                    type: 'InvoiceAction',
                                    subAction,
                                })}
                        />
                    </Tab>
                    <Tab
                        label="Lieferinformationen"
                        className="tabs__tab"
                        value="Delivery"
                    >
                        <DeliveryTabView
                            active={state.activeTab === 'Delivery'}
                            state={state.deliveryTab}
                            dispatch={
                                subAction => dispatch({
                                    type: 'DeliveryAction',
                                    subAction,
                                })}
                        />
                    </Tab>
                    <Tab
                        label="Pakete"
                        className="tabs__tab"
                        value="Parcels"
                    >
                        <ParcelsTabView
                            parcelDispatchers={state.parcelDispatchers}
                            state={state.parcelsTab}
                            dispatch={
                                subAction => dispatch({
                                    type: 'ParcelsAction',
                                    subAction,
                                })}
                        />
                    </Tab>
                    <Tab
                        label="Kundeninformationen"
                        className="tabs__tab"
                        value="CustomerInformation"
                    >
                        <BpsUserInformationTab/>
                    </Tab>
                    <Tab
                        label="Auftragshistorie"
                        className="tabs__tab"
                        value="Protocol"
                    >
                        <ProtocolView
                            orderProtocolsWithDetails={
                                state.orderWithDetails.orderProtocolsWithDetails
                            }
                        />
                    </Tab>
                </Tabs>
            </div>
        )
    )

function ProtocolView({orderProtocolsWithDetails}: {
    orderProtocolsWithDetails: OrderProtocolWithDetails[],
}) {
    return (
        <div className="protocol">
            <div className="card">
                <h2 className="card__header">Auftragshistorie</h2>
                <Divider/>
                {orderProtocolsWithDetails.map(
                    orderProtocolWithDetails => {
                        const {orderProtocol, orderProtocolDetails} = orderProtocolWithDetails
                        return (
                            <div
                                className="protocol__item"
                                key={orderProtocol.id}
                            >
                                <p className="protocol__item-date">
                                    {orderProtocol.at.format(DateTimeFormatter.ISO_LOCAL_DATE_TIME)}
                                </p>
                                <p className="protocol__item-label">
                                    Rechnung versendet
                                </p>
                                <p className="protocol__item-description">
                                    <ul>
                                        {orderProtocolDetails.map(
                                            orderProtocolDetail =>
                                                <li key={orderProtocolDetail.id}>
                                                    {orderProtocolDetailTypeLabel(orderProtocolDetail.type)}:
                                                    {orderProtocolDetail.value}
                                                </li>
                                        )}
                                    </ul>
                                </p>
                                <Divider/>
                            </div>)
                    })}
            </div>
        </div>
    )
}

function orderProtocolDetailTypeLabel(orderProtocolDetailType: OrderProtocolDetailType): string {
    switch (orderProtocolDetailType) {
        case 'To':
            return 'An'
        case 'Cc':
            return 'Kopie'
    }
}

function BpsUserInformationTab() {
    return (
        <div className="tabs__content">
            <div className="flex-column">
                <div className="card tab__card">
                    <form className="tab__form">
                        <div className="tab__form-header">
                            <h4>Grundsätzliche Informationen</h4>
                        </div>
                        <TextField
                            floatingLabelText="Information"
                            disabled={true}
                            value="20% auf Lagerartikel"
                            inputStyle={{color: 'black'}}
                            underlineDisabledStyle={{display: 'none'}}
                        />
                    </form>
                </div>
            </div>
            <div className="flex-column">
                <Card className="card tab__card">
                    <form className="tab__form">
                        <div className="tab__form-header">
                            <h4>Auftragsbezogene Informationen</h4>
                            <EditableButtons
                                editable={{type: 'Locked'}}
                                onEdit={() => void 0}
                                onSave={() => void 0}
                                onDiscard={() => void 0}
                            />
                        </div>
                        <FlatButton
                            label="Hinzufügen"
                            primary={true}
                            icon={<ContentAdd/>}
                            labelPosition="after"
                        />
                    </form>
                </Card>
            </div>
        </div>)
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/Pages/OrderDetails/OrderDetailsView.tsx